import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { storageAdapter } from "@/integrations/supabase/storageAdapter";
import type { Session, User } from "@supabase/supabase-js";

interface Profile {
  id: string;
  username: string | null;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url: string | null;
  plan: "free" | "premium";
  is_blocked: boolean;
  created_at: string;
  daily_goal_minutes: number;
  is_test_user: boolean;
  maintenance_bypass: boolean;
  last_login_at: string | null;
  last_active_at: string | null;
  notification_preferences: {
    email_enabled: boolean;
    email_status_change: boolean;
    email_new_comment: boolean;
  };
  deletion_requested_at: string | null;
  deletion_reason: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  role: "admin" | "user" | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

type PresenceRpcClient = {
  rpc: (fn: "record_presence_heartbeat") => Promise<unknown>;
};

type ProfileActivityClient = {
  from: (table: "profiles") => {
    update: (values: { last_login_at: string }) => {
      eq: (column: "id", value: string) => Promise<unknown>;
    };
  };
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  role: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<"admin" | "user" | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionRef = useRef<Session | null>(null);
  const loginTrackedRef = useRef(false);

  const fetchProfile = useCallback(async (userId: string) => {
    const { data: profileData } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (profileData) {
      if (profileData.is_blocked) {
        await supabase.auth.signOut();
        setSession(null);
        setProfile(null);
        setRole(null);
        return;
      }
      setProfile(profileData as Profile);
    }

    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);

    const roles = roleData?.map((r) => r.role) ?? [];
    setRole(roles.includes("admin") ? "admin" : roles.includes("user") ? "user" : "user");
  }, []);

  const refreshProfile = useCallback(async () => {
    const uid = sessionRef.current?.user?.id;
    if (uid) {
      await fetchProfile(uid);
    }
  }, [fetchProfile]);

  // Track last_login_at on session start
  useEffect(() => {
    if (session?.user && !loginTrackedRef.current) {
      loginTrackedRef.current = true;
      (supabase as unknown as ProfileActivityClient)
        .from("profiles")
        .update({ last_login_at: new Date().toISOString() })
        .eq("id", session.user.id)
        .then();
    }
    if (!session) {
      loginTrackedRef.current = false;
    }
  }, [session]);

  // Heartbeat: update last_active_at every 60s and maintain online peak metrics.
  useEffect(() => {
    if (!session?.user) return;

    const update = () => {
      (supabase as unknown as PresenceRpcClient)
        .rpc("record_presence_heartbeat")
        .then();
    };

    update(); // immediately on mount
    const interval = setInterval(update, 60_000);
    return () => clearInterval(interval);
  }, [session?.user?.id]);

  useEffect(() => {
    let isMounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!isMounted) return;

        if (_event === "PASSWORD_RECOVERY" && !window.location.pathname.startsWith("/reset-password")) {
          window.location.href = "/reset-password" + window.location.hash;
          return;
        }

        setSession(newSession);
        sessionRef.current = newSession;
        if (newSession?.user) {
          fetchProfile(newSession.user.id)
            .then(() => { if (isMounted) setLoading(false); })
            .catch(() => { if (isMounted) setLoading(false); });
        } else {
          setProfile(null);
          setRole(null);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession()
      .then(({ data: { session: s } }) => {
        if (!isMounted) return;
        setSession(s);
        sessionRef.current = s;
        if (s?.user) {
          fetchProfile(s.user.id)
            .then(() => { if (isMounted) setLoading(false); })
            .catch(() => { if (isMounted) setLoading(false); });
        } else {
          setLoading(false);
        }
      })
      .catch(() => { if (isMounted) setLoading(false); });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    storageAdapter.clearAll();
    setSession(null);
    setProfile(null);
    setRole(null);
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        role,
        loading,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
